'use client';

import CustomRePassword from '@/components/custom-re-password';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { cn } from '@/lib/utils';
import { Login } from '@/server/api/v1/auth/auth.action';
import { TokenType } from '@/types/enum';
import { motion } from 'framer-motion';
import { CircleAlert } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { memo, useCallback, useEffect, useRef, useState } from 'react';

type LoginInfo = {
    username: string;
    password: string;
};

type Props = {
    loginInfo: LoginInfo;
    token: string;
    /**Đơn vị là giây */
    expireIn: number;
};

/**Form đổi mật khẩu đối với lần đầu đăng nhập */
export const ChangePasswordForm = memo((props: Props) => {
    const { expireIn, loginInfo, token } = props;

    const transDef = useTranslations('System');
    const transPage = useTranslations('RePassword');

    const _autoExpireRef = useRef<number>(expireIn);

    /**Token dùng để đổi mật khẩu */
    const [tokenOTP, setTokenOTP] = useState(token);
    const [status, setStatus] = useState<{
        isSuccess: boolean;
        isError: boolean;
        message?: string;
    }>({ isError: false, isSuccess: false });

    /**Quay về màn hình đăng nhập */
    const onGoOutToLogin = useCallback(() => {
        window.location.reload();
    }, []);

    /**Lấy lại token xử lý */
    const onReSignInToGetToken = useCallback(async () => {
        const result = await Login(loginInfo);

        if (result?.data?.isSuccessStatus) {
            if (result?.data?.data?.TokenType === TokenType.OTP_PWD) {
                setTokenOTP(result.data.data.Token);
                _autoExpireRef.current = result.data.data.ExpiresIn;

                return;
            }
        } else {
            // Nếu ở đây mà có lỗi thì đẩy ra lại LOGIN
            console.error('failed to login', result?.data);
        }

        window.alert('Đã có lỗi xảy ra, vui lòng thử lại!');
        onGoOutToLogin();
    }, [loginInfo, onGoOutToLogin]);

    /**
     * Interval để tự động out ra ngoài nếu hết time
     */
    useEffect(() => {
        const interval = setInterval(() => {
            if (_autoExpireRef.current <= 0) {
                clearInterval(interval);
                window.alert(
                    'Hết thời gian đổi mật khẩu, vui lòng thực hiện tại!'
                );
                onGoOutToLogin();
                return;
            }

            _autoExpireRef.current -= 1;
        }, 1000);

        return () => clearInterval(interval);
    }, [onGoOutToLogin, token]);

    /**Callback khi xảy ra lỗi trong luồng */
    const callbackError = async (errors: string[], isReloadToken?: boolean) => {
        if (isReloadToken) {
            await onReSignInToGetToken();
        }

        setStatus({
            isSuccess: false,
            isError: true,
            message: errors.join(', ') ?? 'Vui lòng thử lại',
        });
    };

    /**callback khi đổi mật khẩu thành công */
    const callbackSucess = async () => {
        setStatus({
            isSuccess: true,
            isError: false,
            message:
                'Đổi mật khẩu tài khoản thành công. Điều hướng về trang đăng nhập...',
        });
        setTimeout(() => {
            onGoOutToLogin();
        }, 3500);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
        >
            <h2 className='text-center text-3xl font-bold uppercase'>
                {transPage('PasswordChange')}
            </h2>
            <div className='mt-8 space-y-6'>
                <CustomRePassword.WithToken
                    username={loginInfo.username}
                    token={tokenOTP}
                    onSuccess={callbackSucess}
                    onError={callbackError}
                    onClearAlert={() =>
                        setStatus({ isError: false, isSuccess: false })
                    }
                    alert={
                        (status.isError || status.isSuccess) && (
                            <Alert
                                className={cn('py-2', {
                                    'bg-green-100 text-green-600':
                                        status.isSuccess,
                                    'bg-red-100 text-red-600': status.isError,
                                })}
                            >
                                <CircleAlert className={cn('h-4 w-4')} />
                                <AlertTitle className='text-sm'>
                                    {status.isSuccess
                                        ? transPage('ChangePasswordSuccess')
                                        : status.isError
                                          ? transDef(
                                                'System_AnErrorHasOccurred'
                                            )
                                          : ''}
                                </AlertTitle>
                                <AlertDescription className='text-xs'>
                                    {status.isSuccess
                                        ? 'Đang chuyển hướng về trang đăng nhập. . .'
                                        : (status.message ?? '')}
                                </AlertDescription>
                            </Alert>
                        )
                    }
                />
            </div>

            <p
                role='button'
                onClick={onGoOutToLogin}
                className='mt-8 cursor-pointer text-center text-sm text-primary hover:underline'
            >
                Quay về đăng nhập
            </p>
        </motion.div>
    );
});

ChangePasswordForm.displayName = 'ChangePasswordForm';
